import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState, useRef } from 'react';
import { isEmpty } from 'lodash';
import { useSnackbar, Alert, Breadcrumbs, Link, Stack, Skeleton, Typography, Box } from '@emburse/embark-core';
import { SkeletonDataGrid } from '../../components';
import { validateResponse, RejectException } from '../../api/common';
import { poll, PollingTimeoutException } from '../../util/helpers';
import { $t, useTranslation } from '../../hooks/useTranslation';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { getAccount, getCardsByAccount, getConnection, accountIsFullySynced, getPCardsByAccount, mapCard as mapCardAPI, unmapCard as unmapCardAPI } from '../../api/cardsGateway';
import CardEmployeeSearch from './CardEmployeeSearch';
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const urlConnectionId = urlParams.get('connectionId');
const urlAccountId = urlParams.get('accountId');
const POLLING_INTERVAL = 2000;
const POLLING_MAX_ATTEMPTS = 30;
const SLOW_LOADING_TIMEOUT = 30000;
const SYNCING_CONNECTION_MESSAGE = 'Syncing card program data. This may take up to 30 seconds to complete.';
const SLOW_LOADING_MESSAGE = 'Loading card program data is taking longer than usual.';
const POLLING_TIMEOUT_MESSAGE = 'Card program data has not synced yet. An email will be sent once your cards are ready to map.';
const GENERIC_ERROR_LOADING_MESSAGE = 'There was an error loading your cards. Please try again later.';
const NO_CARDS_EMPTY_STATE_TEXT = 'There are no cards to map.';
const NOTICE_TEXT = `Map cards to automatically import card transactions to an employee's account. 
Transactions for unmapped cards will not import to Certify`;
function CorpCardMapping() {
    useTranslation();
    const { closeSnackbar, enqueueSnackbar } = useSnackbar();
    const loadingSnackbarKey = useRef(Date.now());
    const loadTimeMonitor = useRef(null);
    const [loading, setLoading] = useState(true);
    const [isSlowLoad, setIsSlowLoad] = useState(false);
    const [loadingErrorMessage, setLoadingErrorMessage] = useState('');
    const [pCards, setPCards] = useState({});
    const [accounts, setAccounts] = useState([]);
    const [rows, setRows] = useState({});
    const loadCards = useCallback(async (accountId) => {
        const account = await getAccount(accountId)
            .then((resp) => validateResponse(resp))
            .then((resp) => JSON.parse(resp.Data));
        const cards = await getCardsByAccount(accountId)
            .then(validateResponse)
            .then((resp) => JSON.parse(resp.Data));
        setAccounts((currentAccounts) => [...currentAccounts, account]);
        setRows((currentRows) => ({
            ...currentRows,
            [accountId]: cards
        }));
    }, []);
    const loadPCards = useCallback(async (accountId) => {
        const requestedPCards = await getPCardsByAccount(accountId)
            .then(validateResponse)
            .then((resp) => JSON.parse(resp.Data));
        setPCards((currentPCards) => ({
            ...currentPCards,
            [accountId]: requestedPCards
        }));
    }, []);
    const getMappedEmployeeForCard = useCallback((card) => {
        const cardAccountPCards = pCards[card.account.id] || [];
        const mappedPCard = cardAccountPCards.filter((pCard) => pCard.AccountID === card.id)[0];
        if (!mappedPCard) {
            return null;
        }
        return mappedPCard.Employee;
    }, [pCards]);
    const mapCard = useCallback(async (card, employee) => {
        await mapCardAPI(card, employee.PK).then(validateResponse);
        const { panMasked } = card;
        const maskedCardNumber = `\u2022\u2022\u2022\u2022${panMasked.substring(panMasked.length - 4)}`;
        const mappedCardMessage = `Card ${maskedCardNumber} ${$t('successfully mapped to')} ${employee.FullName}`;
        enqueueSnackbar(mappedCardMessage, {
            variant: 'alert',
            AlertProps: { severity: 'success' }
        });
    }, [enqueueSnackbar]);
    const unmapCard = useCallback(async (card) => {
        await unmapCardAPI(card).then(validateResponse);
        const { panMasked } = card;
        const maskedCardNumber = `\u2022\u2022\u2022\u2022${panMasked.substring(panMasked.length - 4)}`;
        const unmappedCardMessage = `Card ${maskedCardNumber} ${$t('has been unmapped')}`;
        enqueueSnackbar(unmappedCardMessage, {
            variant: 'alert',
            AlertProps: { severity: 'success' }
        });
    }, [enqueueSnackbar]);
    const onMapCard = useCallback(async (card, employee) => {
        if (employee) {
            await mapCard(card, employee);
        }
        else {
            await unmapCard(card);
        }
    }, [mapCard, unmapCard]);
    const connectionExists = useCallback(async (connectionId) => {
        const { Data } = await accountIsFullySynced(connectionId).then((resp) => validateResponse(resp));
        return JSON.parse(Data);
    }, []);
    const isAccount = !isEmpty(urlAccountId);
    const isConnection = !isEmpty(urlConnectionId);
    useEffect(() => {
        (async () => {
            try {
                if (!isAccount && !isConnection) {
                    throw new RejectException('Account does not exist');
                }
                // After 30 seconds, trigger the slow loading notification
                loadTimeMonitor.current = setTimeout(() => {
                    setIsSlowLoad(true);
                }, SLOW_LOADING_TIMEOUT);
                let accountIds = [];
                if (isAccount) {
                    accountIds.push(urlAccountId);
                }
                else {
                    // we are waiting for a new gateway connection to sync
                    await poll(() => connectionExists(urlConnectionId), (result) => !result, POLLING_INTERVAL, POLLING_MAX_ATTEMPTS);
                    const { Data: connectionJson } = await getConnection(urlConnectionId);
                    const connection = JSON.parse(connectionJson);
                    accountIds = connection.accounts.map((account) => account.id);
                }
                const cards = accountIds.map((accountId) => loadCards(accountId));
                const pcards = accountIds.map((accountId) => loadPCards(accountId));
                await Promise.all([...cards, ...pcards]);
            }
            catch (err) {
                if (err instanceof RejectException) {
                    setLoadingErrorMessage(err.message);
                }
                else if (err instanceof PollingTimeoutException) {
                    setLoadingErrorMessage(POLLING_TIMEOUT_MESSAGE);
                }
                else {
                    setLoadingErrorMessage(GENERIC_ERROR_LOADING_MESSAGE);
                }
            }
            finally {
                setLoading(false);
            }
        })();
    }, [isAccount, isConnection, loadCards, loadPCards, connectionExists, closeSnackbar]);
    useEffect(() => {
        if (loading) {
            // start load monitoring
            if (isConnection) {
                enqueueSnackbar(SYNCING_CONNECTION_MESSAGE, {
                    variant: 'alert',
                    persist: true,
                    key: loadingSnackbarKey.current,
                    AlertProps: { severity: 'warning' }
                });
            }
        }
        else {
            closeSnackbar(loadingSnackbarKey.current);
            clearTimeout(loadTimeMonitor.current);
        }
    }, [loading, isConnection, enqueueSnackbar, closeSnackbar]);
    useEffect(() => {
        if (loadingErrorMessage) {
            enqueueSnackbar($t(loadingErrorMessage), {
                variant: 'alert',
                persist: true,
                AlertProps: {
                    severity: 'error',
                    action: (_jsx(Stack, { sx: { height: '100%' }, justifyContent: "center", children: _jsx(Link, { underline: "none", href: "/CorpCardManagement.aspx", children: "GO BACK" }) }))
                }
            });
        }
    }, [loadingErrorMessage, enqueueSnackbar]);
    useEffect(() => {
        if (!isSlowLoad) {
            return;
        }
        // closing then cycling the key causes the snackbar to close
        // then a new one to appear when loading text changes. If we
        // don't cycle the key, closeSnackbar will close the
        // original snackbar and the one we just queued
        closeSnackbar(loadingSnackbarKey.current);
        loadingSnackbarKey.current = Date.now();
        enqueueSnackbar(SLOW_LOADING_MESSAGE, {
            variant: 'alert',
            persist: true,
            key: loadingSnackbarKey.current,
            AlertProps: { severity: 'warning' }
        });
    }, [isSlowLoad, enqueueSnackbar, closeSnackbar]);
    const columns = [
        {
            field: 'panMasked',
            headerName: $t('Card Number'),
            flex: 2,
            minWidth: 210,
            renderCell: (cell) => (_jsxs(Stack, { direction: "row", children: [_jsx(Box, { sx: { marginRight: '4px' }, children: "\u2022\u2022\u2022\u2022" }), _jsx(Typography, { children: cell.value.substring(cell.value.length - 4) })] }))
        },
        {
            field: 'cardholder',
            headerName: $t('Cardholder Name'),
            flex: 2,
            minWidth: 210,
            renderCell: (cell) => {
                const fullName = `${cell.value?.firstName || ''} ${cell.value?.lastName || ''}`;
                return _jsx(Typography, { children: fullName });
            }
        },
        {
            field: 'employee',
            headerName: $t('Employee'),
            flex: 8,
            renderCell: (cell) => {
                const card = cell.row;
                const employeeChangeHandler = async (employee) => {
                    await onMapCard(card, employee);
                };
                return (_jsx(CardEmployeeSearch, { employeeChangeHandler: employeeChangeHandler, initialEmployee: getMappedEmployeeForCard(card) }));
            }
        }
    ];
    return (_jsx(SingleColumnLayout, { children: _jsxs(Stack, { direction: "column", alignItems: "flex-start", spacing: 5, sx: { width: '100%' }, children: [_jsxs(Breadcrumbs, { children: [_jsx(Link, { href: "/CorpCardManagement.aspx", color: "textSecondary", children: $t('Card Programs') }), _jsx(Typography, { children: $t('Card Mapping') })] }), _jsxs(Stack, { direction: "column", alignItems: "flex-start", spacing: 2, children: [_jsx(Typography, { variant: "h5", children: "Card Mapping" }), _jsx(Alert, { severity: "info", children: $t(NOTICE_TEXT) })] }), _jsx(_Fragment, { children: (accounts.length ? accounts : [{ id: 'loading' }]).map((account) => (_jsxs(Stack, { direction: "column", alignItems: "flex-start", spacing: 2, sx: {
                            width: '100%'
                        }, children: [loading && (_jsx(Skeleton, { animation: "pulse", variant: "text", width: 200, height: 50 })), !loading && (_jsx(Typography, { variant: "h6", sx: { textStyle: 'bold' }, children: account.name })), _jsx(SkeletonDataGrid, { loading: loading, autoHeight: true, rowHeight: 60, sx: { width: '100%' }, pageSizeOptions: [10, 20, 50], rows: rows[account.id] || [], noRowsEmptyStateProps: {
                                    text: $t(NO_CARDS_EMPTY_STATE_TEXT)
                                }, columns: columns })] }, account.id))) })] }) }));
}
export default CorpCardMapping;
